import PropTypes from "prop-types"
import React, { useState } from "react"

import { sanitize } from "../../../utils/utils"

import ServiceLocation from "./ServiceLocation"

// Icons for the stat blocks
const CashIcon = ({ theme }) => (
  <svg className="h-10 w-10" viewBox="0 0 40 40" fill="none">
    <path
      d="M7 14H33M7 14C6.44772 14 6 13.5523 6 13V9C6 8.44772 6.44772 8 7 8H33C33.5523 8 34 8.44772 34 9V13C34 13.5523 33.5523 14 33 14M7 14V26C7 26.5523 7.44772 27 8 27H32C32.5523 27 33 26.5523 33 26V14M15 23H25M18 19H22"
      stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"}
      strokeWidth="2"
      fill="none"
    />
  </svg>
)

const TimeIcon = ({ theme }) => (
  <svg className="h-10 w-10" viewBox="0 0 40 40" fill="none">
    <circle
      cx="20"
      cy="20"
      r="15"
      stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"}
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M20 10V20L28 28"
      stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const ServiceDetails = ({ service, practice, theme }) => {
  const [showFullDescription, setShowFullDescription] = useState(false)

  const formatPrice = (amountCents) => {
    if (!amountCents) return "Free"

    // Check if service has variations (multiple prices)
    if (service.variations && service.variations.length > 1) {
      const prices = service.variations.map((v) => v.amountCents / 100).sort((a, b) => a - b)
      const minPrice = Math.min(...prices)
      const maxPrice = Math.max(...prices)

      if (minPrice === maxPrice) {
        return `$${minPrice.toFixed(0)}`
      }
      return `$${minPrice.toFixed(0)}-$${maxPrice.toFixed(0)}`
    }

    return `$${(amountCents / 100).toFixed(0)}`
  }

  const formatDuration = () => {
    if (service.variations && service.variations.length > 1) {
      const durations = service.variations.map((v) => v.timeLength).sort((a, b) => a - b)
      const uniqueDurations = [...new Set(durations)]

      if (uniqueDurations.length === 1) {
        return `${uniqueDurations[0]} min`
      }
      return uniqueDurations.join("/") + " Min"
    }

    return service.timeLength ? `${service.timeLength} Min` : "Duration varies"
  }

  const truncateDescription = (text, limit = 200) => {
    if (!text) return ""
    if (text.length <= limit) return text
    return text.substring(0, limit) + "..."
  }

  const displayDescription = showFullDescription ? service.description : truncateDescription(service.description, 200)

  return (
    <div className="flex w-full flex-col items-start justify-start gap-10">
      {/* Service Image */}
      {service.imageUrl && (
        <div
          className="h-64 w-full rounded-xl bg-cover bg-center"
          style={{ backgroundImage: `url(${service.imageUrl})` }}
        />
      )}

      {/* Service Stats */}
      <div className="flex w-full items-center justify-between gap-6">
        {/* Price Stat Block */}
        <div
          className="flex flex-1 flex-col items-center justify-center gap-2 rounded-[32px] p-4"
          style={{
            backgroundColor: theme?.colors?.background || "#ffffff",
            border: `1px solid ${theme?.colors?.border || "#e6e6e6"}`,
            borderRadius: "32px"
          }}>
          <div className="flex h-10 w-10 items-center justify-center">
            <CashIcon theme={theme} />
          </div>
          <div className="flex flex-col items-center justify-start leading-[0]">
            <div
              className="text-[20px] font-semibold leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                color: theme?.colors?.textDark || "#55433f"
              }}>
              {formatPrice(service.amountCents)}
            </div>
            <div
              className="text-[16px] font-normal leading-[24px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                color: theme?.colors?.textDark || "#55433f"
              }}>
              Price
            </div>
          </div>
        </div>

        {/* Duration Stat Block */}
        <div
          className="flex flex-1 flex-col items-center justify-center gap-2 rounded-[32px] p-4"
          style={{
            backgroundColor: theme?.colors?.background || "#ffffff",
            border: `1px solid ${theme?.colors?.border || "#e6e6e6"}`,
            borderRadius: "32px"
          }}>
          <div className="flex h-10 w-10 items-center justify-center">
            <TimeIcon theme={theme} />
          </div>
          <div className="flex flex-col items-center justify-start leading-[0]">
            <div
              className="text-[20px] font-semibold leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                color: theme?.colors?.textDark || "#55433f"
              }}>
              {formatDuration()}
            </div>
            <div
              className="text-[16px] font-normal leading-[24px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                color: theme?.colors?.textDark || "#55433f"
              }}>
              Duration
            </div>
          </div>
        </div>
      </div>

      {/* Service Description */}
      {service.description && (
        <div className="flex w-full flex-col gap-4">
          <h3
            className="text-[24px] font-bold leading-[36px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              color: theme?.colors?.textDark || "#55433f"
            }}>
            About this service
          </h3>
          <div className="flex flex-col gap-4">
            <div
              className="text-[18px] font-normal leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                color: theme?.colors?.textDark || "#55433f"
              }}
              dangerouslySetInnerHTML={{
                __html: sanitize(displayDescription)
              }}
            />
            {service.description.length > 200 && (
              <button
                onClick={() => setShowFullDescription(!showFullDescription)}
                className="text-left font-semibold underline decoration-solid transition-colors hover:no-underline"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: theme?.colors?.primary || "#bd654f"
                }}>
                {showFullDescription ? "Show less" : "Read more"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Service Features/Benefits */}
      {service.features && service.features.length > 0 && (
        <div className="flex w-full flex-col gap-4">
          <h3
            className="text-[24px] font-bold leading-[36px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              color: theme?.colors?.textDark || "#55433f"
            }}>
            What&apos;s included
          </h3>
          <ul className="flex flex-col gap-2">
            {service.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <div
                  className="mt-2 h-2 w-2 rounded-full"
                  style={{ backgroundColor: theme?.colors?.primary || "#bd654f" }}
                />
                <span
                  className="text-[16px] font-normal leading-[24px]"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    color: theme?.colors?.textDark || "#55433f"
                  }}>
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Service Policies */}
      {service.policies && service.policies.length > 0 && (
        <div className="flex w-full flex-col gap-4">
          <h3
            className="text-[24px] font-bold leading-[36px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              color: theme?.colors?.textDark || "#55433f"
            }}>
            Important information
          </h3>
          <ul className="flex flex-col gap-2">
            {service.policies.map((policy, index) => (
              <li key={index} className="flex items-start gap-3">
                <div
                  className="mt-2 h-2 w-2 rounded-full"
                  style={{ backgroundColor: theme?.colors?.primary || "#bd654f" }}
                />
                <span
                  className="text-[16px] font-normal leading-[24px]"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    color: theme?.colors?.textDark || "#55433f"
                  }}>
                  {policy}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Service Location */}
      <ServiceLocation
        locations={service.locations}
        selectedLocation={service.locations?.[0]} // Use first location as default
        practice={practice}
        theme={theme}
      />
    </div>
  )
}

ServiceDetails.propTypes = {
  service: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    amountCents: PropTypes.number,
    timeLength: PropTypes.number,
    imageUrl: PropTypes.string,
    virtual: PropTypes.bool,
    atHome: PropTypes.bool,
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        hide_address: PropTypes.bool
      })
    ),
    variations: PropTypes.arrayOf(PropTypes.object),
    features: PropTypes.arrayOf(PropTypes.string),
    policies: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  practice: PropTypes.object,
  theme: PropTypes.object
}

export default ServiceDetails
